/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable react/react-in-jsx-scope */
import { type State } from '@hookstate/core'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material'
import Map from '~/features/Map/components/Map'
import DrawInMap from '../DrawInMap'
import 'leaflet-draw/dist/leaflet.draw.css'
import { LoadingButton } from '@mui/lab'
import { useLang } from '~/hooks/useLang'
import CloseIcon from '@mui/icons-material/Close'
import {
  getReverseGeocoding,
  getDistrictIdByName,
  LocationPrediction,
  getAutoCompleteOptions,
  getPlaceDetails,
} from '~/utils/location'
import { getCities } from '~/features/City/services'
import type ICity from '~/features/City/interfaces/ICity'
import { useState, useEffect, useRef } from 'react'
import { LatLng, type Map as LeafletMap } from 'leaflet'
import { FetchAutoComplete } from '~/components/FetchAutoComplete'
import { useMap } from 'react-leaflet'

interface ModalDrawMapProps {
  readonly isOpen: State<boolean>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly geographicalArea: any
  readonly lat: State<string>
  readonly long: State<string>
  readonly city?: State<ICity | null>
  readonly district?: State<{ id: string | null; name: string }>
  readonly localTextValue: State<string | null>
}

export default function ModalDrawMap({
  isOpen,
  geographicalArea,
  lat,
  long,
  city,
  district,
  localTextValue,
}: ModalDrawMapProps) {
  const { translate } = useLang()
  // Tipo que acomoda tanto o retorno de details quanto de reverse-geolocation
  type LocationInfo = {
    readonly main: string
    readonly secondary: string
    readonly city: string
    readonly state: string
    readonly country: string
    readonly latitude: number
    readonly longitude: number
    readonly type: string
    readonly district?: string
    readonly description?: string
  }

  const [locationInfo, setLocationInfo] = useState<LocationInfo | null>(null)
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  const [updateFromMap, setUpdateFromMap] = useState(false)
  const [selectedLatLng, setSelectedLatLng] = useState<{
    lat: number
    lng: number
  } | null>(() => {
    const latitude = parseFloat(lat.get())
    const longitude = parseFloat(long.get())
    if (!isNaN(latitude) && !isNaN(longitude)) {
      return { lat: latitude, lng: longitude }
    }
    return null
  })

  // Estado com instância do mapa e flag programática (evita mutações em refs)
  const [leafletMap, setLeafletMap] = useState<LeafletMap | null>(null)
  const [programmaticChange, setProgrammaticChange] = useState(false)

  const latValue = lat.get()
  const longValue = long.get()

  useEffect(() => {
    if (isOpen.get() && !updateFromMap && latValue && longValue) {
      const latitude = parseFloat(latValue)
      const longitude = parseFloat(longValue)

      if (!isNaN(latitude) && !isNaN(longitude)) {
        setIsLoadingLocation(true)
        const latLng = new LatLng(latitude, longitude)
        const { locationPromise, controller } = getReverseGeocoding(latLng)

        locationPromise
          .then(async (response) => {
            const address = response.data as LocationInfo
            setLocationInfo(address)
          })
          .catch((error) => {
            console.error('Erro ao buscar informações de localização:', error)
            setLocationInfo(null)
          })
          .finally(() => {
            setIsLoadingLocation(false)
          })

        return () => controller.abort()
      }
    }
  }, [isOpen.get()])

  // Efeito para buscar dados APENAS quando o usuário interage com o mapa
  useEffect(() => {
    if (updateFromMap) {
      const latitude = parseFloat(latValue)
      const longitude = parseFloat(longValue)

      if (!isNaN(latitude) && !isNaN(longitude)) {
        setIsLoadingLocation(true)
        const latLng = new LatLng(latitude, longitude)
        const { locationPromise, controller } = getReverseGeocoding(latLng)

        locationPromise
          .then(async (response) => {
            const address = response.data as LocationInfo
            setLocationInfo(address)
            geographicalArea.set(address?.description)

            const citiesList = await getCities()
            const foundCity =
              citiesList.find((item) => item.name === address?.city) ?? null
            if (city) city.set(foundCity)

            const districtName = address?.district ?? ''
            const districtId = districtName
              ? await getDistrictIdByName(districtName)
              : null
            if (district) district.set({ id: districtId, name: districtName })
          })
          .catch((error) => {
            console.error('Erro ao buscar informações de localização:', error)
            setLocationInfo(null)
          })
          .finally(() => {
            setIsLoadingLocation(false)
            setUpdateFromMap(false) // Reseta a flag
          })

        return () => controller.abort()
      }
    }
  }, [updateFromMap, latValue, longValue])

  // Efeito para desenhar o marcador no mapa quando a localização estiver pronta
  useEffect(() => {
    if (
      locationInfo &&
      drawInMapRef.current?.handleDrawWithCreate &&
      !updateFromMap
    ) {
      // Se veio de uma seleção programática (autocomplete),
      // apenas desenhamos e resetamos a flag sem acionar reverse.
      if (programmaticChange) {
        drawInMapRef.current.handleDrawWithCreate({
          lat: locationInfo.latitude,
          lng: locationInfo.longitude,
        })
        setProgrammaticChange(false)
      } else {
        // Caso contrário, apenas refletimos a localização atual
        drawInMapRef.current.handleDrawWithCreate({
          lat: locationInfo.latitude,
          lng: locationInfo.longitude,
        })
      }
    }
  }, [locationInfo])

  // Atualize o valor do autocomplete quando locationInfo mudar
  useEffect(() => {
    if (locationInfo) {
      localTextValue.set(
        locationInfo.secondary
          ? `${locationInfo.main}, ${locationInfo.secondary}`
          : locationInfo.main
      )
    }
  }, [locationInfo])

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      isOpen.set(false)
    }
  }

  const handleClose = () => {
    isOpen.set(false)
    geographicalArea.set([])
    lat.set('')
    long.set('')
    setLocationInfo(null)
    setSelectedLatLng(null)
    localTextValue.set('')
  }


  function MapCenterWatcher({ onChange }) {
    const map = useMap()

    useEffect(() => {
      setLeafletMap(map as unknown as LeafletMap)
    }, [map])

    useEffect(() => {
      const handleMove = () => {
        const center = map.getCenter()
        onChange({ lat: center.lat, lng: center.lng })
      }
      map.on('moveend', handleMove)
      handleMove()
      return () => {
        map.off('moveend', handleMove)
      }
    }, [map, onChange])

    return null
  }

  const drawInMapRef = useRef<{ handleDrawWithCreate?: (pos: { lat: number; lng: number }) => void } | null>(null)

  return (
    <Dialog
      fullWidth
      maxWidth={'lg'}
      open={isOpen?.get()}
      disableEscapeKeyDown={true}
      onClose={clickOutModalClose}
    >
      <DialogTitle
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        {translate('select point')}
        <IconButton aria-label="close" onClick={() => isOpen.set(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <FetchAutoComplete<LocationPrediction>
            fetchFunc={(input) =>
              getAutoCompleteOptions(input, {
                latLng: selectedLatLng
                  ? new LatLng(selectedLatLng.lat, selectedLatLng.lng)
                  : undefined,
              })
            }
            setFunc={async (selected) => {
              const opt = await getPlaceDetails(selected.placeId)
              if (opt?.latitude && opt?.longitude) {
                // Marca que o próximo desenho no mapa é programático (evita reverse)
                setProgrammaticChange(true)
                // Preenche a informação de localização a partir do details
                setLocationInfo({
                  main: opt.main,
                  secondary: opt.secondary,
                  city: opt.city,
                  state: opt.state,
                  country: opt.country,
                  latitude: opt.latitude,
                  longitude: opt.longitude,
                  type: opt.type,
                })

                // Atualiza lat/long nos estados
                lat.set(String(opt.latitude))
                long.set(String(opt.longitude))

                setSelectedLatLng({ lat: opt.latitude, lng: opt.longitude })

                // Aplica zoom no mapa para a localização selecionada
                if (leafletMap) {
                  leafletMap.setView([opt.latitude, opt.longitude], 16, {
                    animate: true,
                    duration: 1
                  })
                }

                // Não desenhamos manualmente aqui para evitar duplo onChange;
                // o efeito de locationInfo fará o desenho com a flag acima.

                // Atualiza Cidade com base no details
                try {
                  const citiesList = await getCities()
                  const foundCity =
                    citiesList.find((item) => item.name === opt.city) ?? null
                  if (city) city.set(foundCity)
                } catch (err) {
                  console.error('Erro ao atualizar cidade via details', err)
                }
              }
              return Promise.resolve({ ended: false, value: opt?.main || '' })
            }}
            setValueInput={(opt) => opt?.description || ''}
            stateValue={localTextValue}
            noOptionsString="No Locations, yet..."
            optionKey="description"
            autoCompleteProps={{
              getOptionLabel: (item) => item?.description || '',
              placeholder: 'Localização',
              size: 'small',
              errorMsg: false,
              isOptionEqualToValue: (option, value) =>
                option?.description === value?.description,
            }}
            isValid
            endIcon
          />
        </Box>

        <Map height="500px" enableLayerControl={false}>
          <MapCenterWatcher onChange={setSelectedLatLng} />
          <DrawInMap
            ref={drawInMapRef}
            value={geographicalArea.get() ?? []}
            onChange={(value) => {
              if (Array.isArray(value) && value.length === 0) {
                geographicalArea.set([])
                lat.set('')
                long.set('')
                setLocationInfo(null)
                setSelectedLatLng(null)
              } else if (
                value &&
                typeof value === 'object' &&
                'lat' in value &&
                'lng' in value
              ) {
                geographicalArea.set(value)
                lat.set(String(value.lat))
                long.set(String(value.lng))
                setSelectedLatLng({ lat: value.lat, lng: value.lng })
                // Se a alteração veio de uma ação programática (ex.: autocomplete),
                // não devemos disparar o reverse-geocoding
                if (programmaticChange) {
                  setProgrammaticChange(false)
                } else {
                  setUpdateFromMap(true)
                }
              }
            }}
          />
        </Map>

        {/* Informações de localização */}
        <Box
          sx={{
            mt: 2,
            borderRadius: 1,
          }}
        >
          {isLoadingLocation ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2">
                {translate('Loading location information...')}
              </Typography>
            </Box>
          ) : locationInfo ? (
            <Box
              sx={{
                backgroundColor: 'transparent',
                width: 'fit-content',
                padding: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 'bold', mb: 1 }}
              >
                {translate('Location Information')}:
              </Typography>
              <Typography variant="body2">
                <strong>{translate('Address')}:</strong> {locationInfo.main}
                {locationInfo.secondary && `, ${locationInfo.secondary}`}
              </Typography>
              {locationInfo.district && (
                <Typography variant="body2">
                  <strong>{translate('District')}: </strong>
                  {locationInfo.district}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>{translate('City')}:</strong> {locationInfo.city}
              </Typography>
              <Typography variant="body2">
                <strong>{translate('State')}:</strong> {locationInfo.state}
              </Typography>
              {/* <Typography variant="body2">
                <strong>{translate('Coordinates')}:</strong>
                {locationInfo.latitude.toFixed(6)},
                {locationInfo.longitude.toFixed(6)}
              </Typography> */}
            </Box>
          ) : undefined}
        </Box>
      </DialogContent>
      <DialogActions sx={{ paddingRight: '24px', paddingBottom: '24px' }}>
        <Button variant="outlined" onClick={() => handleClose()}>
          {translate('Cancel')}
        </Button>
        <LoadingButton variant="contained" onClick={() => isOpen.set(false)}>
          {translate('Save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}